// Skeleton loaders — shape-matched per view so the eye lands on the same
// spot as the real content. Uses the global .ss-shimmer class for a
// gradient sweep instead of a flat pulse (Insta/Stripe/Linear pattern).

interface SkeletonRowProps {
  count?: number;
}

/** Conversation-row skeleton (avatar + two text lines + meta). */
export function SkeletonRow({ count = 1 }: SkeletonRowProps) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="flex gap-3 border-b border-ink-100 bg-white px-4 py-3.5 dark:border-[#1E293B] dark:bg-[#0F172A]"
          aria-hidden="true"
        >
          <div className="ss-shimmer h-11 w-11 flex-shrink-0 rounded-full" />
          <div className="flex-1 space-y-2 py-1">
            <div className="flex items-center justify-between">
              <div className="ss-shimmer h-3.5 w-32 rounded" />
              <div className="ss-shimmer h-3 w-10 rounded" />
            </div>
            <div className="ss-shimmer h-3 w-3/4 rounded" />
          </div>
        </div>
      ))}
    </>
  );
}

/** Today-screen skeleton — matches greeting + hero + stat row + activity. */
export function SkeletonToday() {
  return (
    <div className="bg-ink-50 dark:bg-[#0B1220]" aria-hidden="true">
      <div className="px-5 pt-4">
        <div className="ss-shimmer mb-1 h-3.5 w-32 rounded" />
        <div className="ss-shimmer h-7 w-44 rounded" />
      </div>
      <div className="ss-shimmer mx-4 mt-4 h-[112px] rounded-ss-lg" />
      <div className="grid grid-cols-3 gap-2.5 px-4 py-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="ss-shimmer h-[68px] rounded-ss-md" />
        ))}
      </div>
      <div className="px-5 pb-2 pt-2">
        <div className="ss-shimmer h-3 w-24 rounded" />
      </div>
      <div className="space-y-2 px-4">
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="ss-shimmer h-[60px] rounded-ss-md" />
        ))}
      </div>
      <div className="px-5 pb-2 pt-4">
        <div className="ss-shimmer h-3 w-28 rounded" />
      </div>
      <SkeletonRow count={3} />
    </div>
  );
}

/** Reports-screen skeleton — period chips + 4 stat cards + chart bars. */
export function SkeletonReports() {
  return (
    <div className="bg-ink-50 px-4 pt-4 pb-8 dark:bg-[#0B1220]" aria-hidden="true">
      <div className="ss-shimmer h-7 w-32 rounded" />
      <div className="mt-3 flex gap-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="ss-shimmer h-8 w-16 rounded-full" />
        ))}
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="ss-shimmer h-[112px] rounded-ss-md" />
        ))}
      </div>
      <div className="ss-shimmer mt-4 h-[180px] rounded-ss-md" />
    </div>
  );
}

/** Conversation-detail skeleton — header + 4-action grid + thread. */
export function SkeletonDetail() {
  return (
    <div className="bg-ink-50 pb-32 dark:bg-[#0B1220]" aria-hidden="true">
      <div className="sticky top-0 flex items-center gap-3 border-b border-ink-200 bg-white px-4 py-3 dark:border-[#334155] dark:bg-[#0F172A]">
        <div className="ss-shimmer h-6 w-6 rounded-full" />
        <div className="flex-1 space-y-1.5">
          <div className="ss-shimmer h-4 w-32 rounded" />
          <div className="ss-shimmer h-3 w-44 rounded" />
        </div>
      </div>
      <div className="grid grid-cols-4 gap-2 px-2 pt-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="ss-shimmer h-[64px] rounded-ss-md" />
        ))}
      </div>
      <div className="px-4 pt-6 space-y-3">
        <div className="ss-shimmer ml-auto h-12 w-2/3 rounded-2xl" />
        <div className="ss-shimmer h-16 w-3/4 rounded-2xl" />
        <div className="ss-shimmer ml-auto h-10 w-1/2 rounded-2xl" />
      </div>
    </div>
  );
}

/** Leads-screen skeleton — 4 collapsible stage groups. */
export function SkeletonLeads() {
  return (
    <div className="space-y-3 px-2 pt-4 pb-8" aria-hidden="true">
      <div className="px-2">
        <div className="ss-shimmer h-7 w-24 rounded" />
        <div className="ss-shimmer mt-2 h-3 w-48 rounded" />
      </div>
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          className="overflow-hidden rounded-2xl border border-ink-100 bg-white dark:border-[#1E293B] dark:bg-[#0F172A]"
        >
          <div className="flex items-center gap-2 px-4 py-3.5">
            <div className="ss-shimmer h-4 w-24 rounded" />
            <div className="ss-shimmer h-5 w-6 rounded-full" />
          </div>
        </div>
      ))}
    </div>
  );
}
